import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { Button } from '../core/Button';

export type ErrorStateVariant = 'page' | 'table' | 'kpi' | 'cards';

/** The four placements differ only in scale — `page` is the largest. */
const LAYOUT: Record<
  ErrorStateVariant,
  { root: string; icon: string; title: string; message: string }
> = {
  page: { root: 'gap-5 px-8 py-20', icon: 'size-14', title: 'text-2xl', message: 'text-base' },
  table: { root: 'gap-4 px-6 py-14', icon: 'size-10', title: 'text-lg', message: 'text-sm' },
  kpi: { root: 'gap-3 px-4 py-8', icon: 'size-8', title: 'text-sm', message: 'text-xs' },
  cards: { root: 'gap-4 px-6 py-12', icon: 'size-10', title: 'text-lg', message: 'text-sm' },
};

export interface ErrorStateProps extends Omit<ComponentPropsWithRef<'div'>, 'title' | 'children'> {
  variant?: ErrorStateVariant;
  icon?: ReactNode;
  title?: string;
  message?: string;
  /** The retry button renders only when this is supplied — a failure with no
   *  recovery path shows no button rather than a dead one. */
  onRetry?: () => void;
  retryLabel?: string;
}

/**
 * The shared failure surface. The container is the live region: `role="alert"`
 * announces the failure when it replaces content, and the message is real text
 * — never a colour or an icon alone.
 */
export function ErrorState({
  variant = 'page',
  icon,
  title,
  message,
  onRetry,
  retryLabel = 'Retry',
  className,
  ...rest
}: ErrorStateProps) {
  const layout = LAYOUT[variant];

  return (
    <div
      {...rest}
      role="alert"
      className={cn(
        'flex w-full flex-col items-center justify-center text-center',
        layout.root,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-grid shrink-0 place-items-center text-danger [&>svg]:size-full',
          layout.icon,
        )}
      >
        {icon ?? <IconAlertTriangle />}
      </span>

      {title ? (
        <p
          className={cn(
            'font-display font-semibold uppercase tracking-title text-heading',
            layout.title,
          )}
        >
          {title}
        </p>
      ) : null}

      {message ? <p className={cn('max-w-prose text-muted', layout.message)}>{message}</p> : null}

      {onRetry ? (
        <Button variant="outline" size="md" leadingIcon={<IconRefresh />} onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
